import React from 'react';
import { TableCell, TableRow, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { GhgSupplierSurveySummaryWithCountry } from '../Scope3GoodsAndServices/hooks';

const dateFormat = 'yyyy-MM-dd';
// The one reason code that carries a free-text explanation (D3).
const OTHER_REASON = 'other';

// C18 Goal 2 (R-16/R-17): additive commitment/target status. A local
// extension (same pattern GhgSupplierSurveySummaryWithCountry itself uses
// for country_codes/branch_descriptions) rather than widening that shared
// file, which sits outside this component's Affected Files -- the fields
// are optional so a plain GhgSupplierSurveySummaryWithCountry value (which
// lacks them structurally) still satisfies this type; the backend always
// sends them now. RG 28.08 5.2/5.3 (K4) added the decline reasons and the
// declined target state:
//  - commitment_status: 'accepted' | 'declined' | 'none'
//  - target_status:     'set' | 'declined' | 'none'
export interface GhgSupplierSurveySummaryWithCommitment extends GhgSupplierSurveySummaryWithCountry {
  commitment_status?: string;
  commitment_date?: string | null;
  commitment_decline_reason?: string | null;
  commitment_decline_reason_text?: string | null;
  target_status?: string;
  target_base_year?: number | null;
  target_year?: number | null;
  target_declined_at?: string | null;
  target_decline_reason?: string | null;
  target_decline_reason_text?: string | null;
}

interface Props {
  survey: GhgSupplierSurveySummaryWithCommitment;
}

export default function SupplierSummaryRow({ survey }: Props) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'dashboard.supplier-summary-table',
  });
  // The reason labels live in the shared `ghg.declineReason.codes` dictionary
  // (the same one the decline form uses), outside this table's key prefix.
  const { t: tCommon, i18n } = useTranslation();

  const formatDate = (
    date: string | null | undefined,
    suffix?: string | null
  ) => {
    const formattedDate = !!date ? format(new Date(date), dateFormat) : '-';
    return suffix ? `${formattedDate} (${suffix})` : formattedDate;
  };

  const formatPercentage = (percentage: number | null | undefined) => {
    return percentage ? `${percentage.toFixed(0)}%` : '-';
  };

  // R-18: translated description(s)/ISO code(s), never the raw branch slug
  // -- same '-' fallback the old single Branches column used for "no
  // branches at all", kept for visual consistency with the rest of this
  // row (every other missing value here also dashes rather than blanks).
  const formatList = (items: string[] | null | undefined) =>
    items && items.length > 0 ? items.join(', ') : '-';

  // RG 28.08 D3: "Declined" plus the reason the carrier gave, from the
  // shared dictionary -- never the raw code. A code this build has no label
  // for (e.g. a list that changed since the decision was stored) degrades to
  // plain "Declined". The free text of "other" is too long for a cell, so it
  // goes into a Tooltip on the label.
  const formatDeclined = (
    declinedLabel: string,
    reason: string | null | undefined,
    reasonText: string | null | undefined
  ): React.ReactNode => {
    const reasonKey = `ghg.declineReason.codes.${reason}`;
    if (!reason || !i18n.exists(reasonKey)) return declinedLabel;
    const text = `${declinedLabel} – ${String(tCommon(reasonKey))}`;
    if (reason === OTHER_REASON && reasonText) {
      return (
        <Tooltip title={reasonText}>
          <span>{text}</span>
        </Tooltip>
      );
    }
    return text;
  };

  // C18 Goal 2: plain text, no chips/meters (scope guardrail: "same
  // switches, plain text values"). RG 28.08 5.2: a carrier who has not
  // decided yet gets an EMPTY cell -- not "Not asked", because the backend
  // cannot tell "never saw the pop-up" from "ignored it".
  const formatCommitmentStatus = (): React.ReactNode => {
    if (survey.commitment_status === 'accepted')
      return String(t('commitment-accepted', 'Accepted'));
    if (survey.commitment_status === 'declined')
      return formatDeclined(
        String(t('commitment-declined', 'Declined')),
        survey.commitment_decline_reason,
        survey.commitment_decline_reason_text
      );
    return null;
  };

  // The base->target year range when a target is set (it wins even if the
  // Letter was declined afterwards), "Declined" + reason after a declined
  // target (5.3). "Not set" only means something once the Letter is
  // accepted -- before that the target is not yet on the table, so the cell
  // stays empty (5.2).
  const formatTargetStatus = (): React.ReactNode => {
    if (
      survey.target_status === 'set' &&
      survey.target_base_year &&
      survey.target_year
    ) {
      return `${survey.target_base_year} → ${survey.target_year}`;
    }
    if (survey.target_status === 'declined') {
      return formatDeclined(
        String(t('target-declined', 'Declined')),
        survey.target_decline_reason,
        survey.target_decline_reason_text
      );
    }
    if (survey.commitment_status === 'accepted') {
      return String(t('target-none', 'Not set'));
    }
    return null;
  };

  return (
    <TableRow key={`${survey.vat_identifier}-${survey.reporting_period}`}>
      <TableCell />
      <TableCell>{survey.vat_identifier}</TableCell>
      <TableCell>{formatList(survey.country_codes)}</TableCell>
      <TableCell>{formatList(survey.branch_descriptions)}</TableCell>
      <TableCell>{survey.reporting_period}</TableCell>
      <TableCell>{formatDate(survey.registered_at)}</TableCell>
      <TableCell>{formatPercentage(survey.progress_percentage)}</TableCell>
      <TableCell>{survey.scope_1_entries}</TableCell>
      <TableCell>{survey.scope_2_entries}</TableCell>
      <TableCell>
        {formatDate(
          survey.esg_questionnaire_submitted_at,
          survey.esg_questionnaire_type
        )}
      </TableCell>
      <TableCell>
        {formatDate(survey.environment_questionnaire_submitted_at)}
      </TableCell>
      <TableCell>{formatCommitmentStatus()}</TableCell>
      <TableCell>{formatTargetStatus()}</TableCell>
    </TableRow>
  );
}
